/**
 * matter — preventa (Magical Preorder) dentro de la vista rápida.
 *
 * La app sólo monta su UI en la página de producto: su custom element lee
 * `current_product` (vacío fuera de la PDP) y se engancha a DOMContentLoaded,
 * así que el modal de vista rápida —que se inyecta después— se queda con el
 * botón normal de "Añadir al carrito".
 *
 * Aquí replicamos su misma decisión, con sus mismos datos:
 *   1. la variante tiene que estar listada en una regla vigente (payload de la
 *      app en la página). Ojo: hay variantes con el metafield viejo apuntando a
 *      reglas que ya no existen —por eso no basta con el metafield—;
 *   2. y su metafield `magical_preorder.stage` tiene que traer un stage
 *      distinto de "none".
 * Sólo entonces cambia el botón, y al agregar al carrito se mandan las mismas
 * propiedades de línea que usa la app (_rule_id + etiqueta de preventa) para
 * que el pedido quede identificado igual que si se hubiera hecho desde la PDP.
 */
(function () {
  var MODAL = '[data-quick-view-modal]';
  var STAGES = '[data-quick-preorder-stages]';
  var datos = null;

  function datosApp() {
    if (datos) return datos;
    var node = document.querySelector('magical-preorder script[type="application/json"]');
    if (!node) return null;

    try {
      var payload = JSON.parse(node.textContent);
      var porVariante = {};

      (payload.rules || []).forEach(function (regla) {
        var productos = (regla.logic && regla.logic.products) || [];
        productos.forEach(function (producto) {
          (producto.variants || []).forEach(function (variante) {
            porVariante[String(variante.id)] = regla;
          });
        });
      });

      datos = {
        textos: (payload.display_text && payload.display_text.general) || {},
        reglaPorVariante: porVariante,
      };
    } catch (e) {
      datos = null;
    }

    return datos;
  }

  function stagesFor(root) {
    var node = root.querySelector(STAGES);
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      return null;
    }
  }

  function setProp(form, name, value) {
    var input = form.querySelector('input[data-preorder-prop][name="' + name + '"]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.setAttribute('data-preorder-prop', '');
      form.appendChild(input);
    }
    if (input.value !== String(value)) input.value = value;
  }

  function clearProps(form, conservar) {
    form.querySelectorAll('input[data-preorder-prop]').forEach(function (el) {
      // Importante: sólo borramos lo que sobra. Si borráramos y volviéramos a
      // crear en cada pasada, el MutationObserver se dispararía de nuevo con
      // cada cambio y el modal se quedaría girando en un bucle.
      if (!conservar || conservar.indexOf(el.name) === -1) el.remove();
    });
  }

  function setLabel(label, value) {
    if (value && label.textContent.trim() !== value) label.textContent = value;
  }

  /**
   * El aviso de alta demanda de la app, debajo del botón. Antes vivía en la
   * descripción del producto (que es por producto, no por variante); así sólo
   * se ve en las variantes que de verdad están en preventa.
   */
  function setAviso(root, html) {
    var contenedor = root.querySelector('.quick-product--right-viewport--footer') || root;
    var aviso = contenedor.querySelector('[data-preorder-message]');

    if (!html) {
      if (aviso) aviso.remove();
      return;
    }

    if (!aviso) {
      aviso = document.createElement('div');
      aviso.className = 'quick-product__preorder-message rte fs-body-75';
      aviso.setAttribute('data-preorder-message', '');
      contenedor.appendChild(aviso);
    }

    if (aviso.innerHTML !== html) aviso.innerHTML = html;
  }

  function apply(root) {
    var stages = stagesFor(root);
    if (!stages) return;

    var form = root.querySelector('form[action*="/cart/add"]');
    var button = root.querySelector('[data-add-to-cart]');
    var label = root.querySelector('[data-add-to-cart-text]');
    if (!form || !button || !label) return;

    var idInput = form.querySelector('[name="id"]');
    var variantId = idInput ? String(idInput.value) : '';
    var info = stages[variantId];
    var app = datosApp();
    var regla = app && app.reglaPorVariante[variantId];
    var stage = info && info.stage;

    // La app pide las dos cosas: regla vigente y stage distinto de "none".
    var enPreventa = !!regla && !!stage && stage !== 'none';

    if (!enPreventa) {
      if (button.hasAttribute('data-preorder-patched')) {
        button.removeAttribute('data-preorder-patched');
        clearProps(form);
        if (button.dataset.langAvailable) setLabel(label, button.dataset.langAvailable);
        button.disabled = false;
        button.removeAttribute('aria-disabled');
      }
      setAviso(root, null);
      return;
    }

    var t = (app && app.textos) || {};

    // El mismo mensaje que la app muestra en la ficha, según la etapa.
    setAviso(
      root,
      (stage === 'preview' ? regla.message_preview : stage === 'closed' ? regla.message_closed : regla.message_active) || null
    );

    if (stage === 'active') {
      var nombres = ['properties[_rule_id]'];
      if (t.preorderLabel) nombres.push('properties[' + t.preorderLabel + ']');
      clearProps(form, nombres);
      setProp(form, 'properties[_rule_id]', regla.id);
      if (t.preorderLabel) setProp(form, 'properties[' + t.preorderLabel + ']', '​');
      setLabel(label, t.preorderButton || 'Compra y aparta tu producto');
      if (button.disabled) button.disabled = false;
      button.removeAttribute('aria-disabled');
    } else {
      clearProps(form);
      setLabel(
        label,
        stage === 'preview'
          ? t.comingSoonButton || 'Próximamente...'
          : t.closedStateButton || 'Venta finalizada'
      );
      if (!button.disabled) button.disabled = true;
      if (button.getAttribute('aria-disabled') !== 'true') button.setAttribute('aria-disabled', 'true');
    }

    // Sólo si hace falta: cada setAttribute genera una mutación y volvería a
    // despertar al observer.
    if (!button.hasAttribute('data-preorder-patched')) button.setAttribute('data-preorder-patched', '');
  }

  function watch(modal) {
    var opciones = {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['value', 'class', 'data-option-value'],
      characterData: true,
    };

    // Sin debounce: el objetivo es corregir el botón en el mismo frame en que
    // el tema inyecta el modal, para que no se vea el cambio de texto. El
    // observer se desconecta mientras aplicamos para que nuestros propios
    // cambios no lo vuelvan a disparar.
    var observer = new MutationObserver(function () {
      observer.disconnect();
      try {
        apply(modal);
      } finally {
        observer.takeRecords();
        observer.observe(modal, opciones);
      }
    });

    observer.observe(modal, opciones);
    apply(modal);
  }

  function init() {
    datosApp();

    var modal = document.querySelector(MODAL);
    if (modal) return watch(modal);

    // El modal vive en el layout, pero por si acaso se monta después
    var observer = new MutationObserver(function () {
      var found = document.querySelector(MODAL);
      if (found) {
        observer.disconnect();
        watch(found);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
